import React, { useState } from "react";
import { Plus, Pencil, Truck, Wrench, BookOpen } from "lucide-react";
import { Chip, EmptyState, Loading, ErrorState } from "../ui/primitives.jsx";
import { AttachThumb, AttachViewer } from "../ui/attachments.jsx";
import { fmtMoney, fmtDate } from "../lib/format.js";
import { useEquipment, useRentalsForStatus, equipmentLiveStatus } from "./api.js";
import EquipmentForm from "./EquipmentForm.jsx";

const STATUS_LABEL = {
  out: "On rent",
  reserved: "Reserved",
  available: "Available",
  maintenance: "Maintenance",
  retired: "Retired",
};

export default function EquipmentTab({ onGoToTab }) {
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const equipment = useEquipment();
  const rentals = useRentalsForStatus();

  return (
    <>
      <div className="card">
        <div className="card-h">
          <h2>Fleet inventory</h2>
          <div className="spacer" />
          <button className="btn primary" onClick={() => setEditing({})}>
            <Plus size={16} /> Add machine
          </button>
        </div>

        <div className="card-b tight" style={{ overflowX: "auto" }}>
          {equipment.isPending ? (
            <Loading what="the fleet" />
          ) : equipment.error ? (
            <ErrorState error={equipment.error} onRetry={() => equipment.refetch()} />
          ) : equipment.data.length === 0 ? (
            <EmptyState
              title="No equipment yet"
              sub="Add your first machine to start booking rentals."
              action={
                <button className="btn primary" onClick={() => setEditing({})}>
                  <Plus size={16} /> Add machine
                </button>
              }
            />
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th></th>
                  <th>Machine</th>
                  <th>Rates (day / wk / mo)</th>
                  <th>Hours</th>
                  <th>Status today</th>
                  <th>Next service</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {equipment.data.map((e) => {
                  // While rentals are still loading, fall back to the stored
                  // status rather than briefly claiming a machine is available.
                  const s = rentals.data ? equipmentLiveStatus(e, rentals.data) : e.status;
                  return (
                    <tr key={e.id}>
                      <td style={{ width: 62 }}>
                        {e.photo_path ? (
                          <AttachThumb
                            path={e.photo_path}
                            onClick={() => setViewing({ path: e.photo_path, name: e.name })}
                          />
                        ) : (
                          <div
                            className="thumb"
                            style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted)" }}
                          >
                            <Truck size={20} />
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="main-cell">{e.name}</div>
                        <div className="sub-cell">
                          {e.category}
                          {e.serial ? " · SN " + e.serial : ""}
                          {e.year ? " · " + e.year : ""}
                        </div>
                      </td>
                      <td>
                        {fmtMoney(e.day_rate)} / {fmtMoney(e.week_rate)} / {fmtMoney(e.month_rate)}
                      </td>
                      <td>{e.hour_meter || "—"}</td>
                      <td><Chip kind={s}>{STATUS_LABEL[s] ?? s}</Chip></td>
                      <td>
                        {e.next_service_date
                          ? fmtDate(e.next_service_date)
                          : e.next_service_hours
                            ? "at " + e.next_service_hours + " hrs"
                            : "—"}
                      </td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {e.manual_path && (
                          <button
                            className="iconbtn"
                            title={"User manual: " + (e.manual_name ?? "")}
                            onClick={() => setViewing({ path: e.manual_path, name: e.manual_name })}
                          >
                            <BookOpen size={16} />
                          </button>
                        )}
                        <button className="iconbtn" title="Maintenance log" onClick={() => onGoToTab?.("maintenance")}>
                          <Wrench size={16} />
                        </button>
                        <button className="iconbtn" title="Edit" onClick={() => setEditing(e)}>
                          <Pencil size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editing !== null && <EquipmentForm eq={editing} onClose={() => setEditing(null)} />}
      {viewing && (
        <AttachViewer path={viewing.path} name={viewing.name} onClose={() => setViewing(null)} />
      )}
    </>
  );
}
